import React, { useState, useMemo, useEffect, useRef, useCallback } from "react"
import {
  Send, Mail, MessageSquare, Phone, Plus, X, Loader2, Upload,
  CheckCircle2, AlertCircle, FileText, ArrowLeft, ArrowRight,
  Settings, Clock, RefreshCw, Flag, Users, Edit, Trash2,
  Pencil, Zap, Layers, BarChart3, ChevronRight, Search,
  MoreHorizontal, Download, Play, Pause, Activity, XCircle, PhoneOff
} from "lucide-react"

import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Badge } from "../ui/badge"
import { toast } from "sonner"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table"
import { ScrollArea } from "../ui/scroll-area"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs"
import { Progress } from "../ui/progress"
import API from "../api/api01"
import { ENDPOINTS } from "../config/endpoints"
import { motion, AnimatePresence } from "framer-motion"
import { cn } from "../ui/utils"
import { useLocation, useNavigate } from "react-router-dom"

// --- Types ---
type CampaignType = 'email' | 'sms' | 'whatsapp'
type CampaignStatus = 'active' | 'sending' | 'dispatched' | 'completed' | 'paused' | 'failed'

interface BulkCampaign {
  id: string
  name: string
  campaign_type: CampaignType
  status: CampaignStatus
  total_contacts: number
  completed_contacts: number
  failed_contacts: number
  subject?: string
  body: string
  whatsapp_template_name?: string
  image_url?: string
  created_at: string
}

interface CampaignContact {
  name?: string
  identifier: string
  status: 'queued' | 'sent' | 'failed' | 'delivered'
  error_message?: string
  sent_at?: string
}

const STEPS = [
  { id: 1, label: "Campaign Identity", icon: Settings },
  { id: 2, label: "Message Assets", icon: FileText },
  { id: 3, label: "Import Recipients", icon: Users },
  { id: 4, label: "Execution Preview", icon: CheckCircle2 },
]

export default function BulkCommunication() {
  const [campaigns, setCampaigns] = useState<BulkCampaign[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [dashboardSearch, setDashboardSearch] = useState("")
  const [dashboardFilter, setDashboardFilter] = useState("all")
  const [view, setView] = useState<'list' | 'detail'>('list')
  const [showWizard, setShowWizard] = useState(false)
  const [campaignToEdit, setCampaignToEdit] = useState<BulkCampaign | null>(null)
  const [selectedCampaign, setSelectedCampaign] = useState<BulkCampaign | null>(null)
  const [reportData, setReportData] = useState<any>(null)
  const [isLoadingReport, setIsLoadingReport] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()

  // Load Campaigns
  useEffect(() => {
    fetchCampaigns()

    // Auto-open wizard if coming from CRM leads
    if (location.state?.selectedContacts?.length > 0) {
      setShowWizard(true)
    }
  }, [location.state])

  // Global Auto-refresh for Active Campaigns
  useEffect(() => {
    const hasActiveCampaigns = campaigns.some(c => c.status === 'sending' || c.status === 'dispatched');
    let interval: any;

    if (hasActiveCampaigns) {
      interval = setInterval(() => {
        fetchCampaigns();
      }, 3000); // Polling every 3 seconds for active campaigns
    } else {
      interval = setInterval(() => {
        fetchCampaigns();
      }, 10000); // Standard 10s refresh for idle state
    }

    return () => clearInterval(interval);
  }, [campaigns]);

  const fetchCampaigns = async () => {
    try {
      const res = await API.get(ENDPOINTS.BULK_COMMUNICATION.CAMPAIGNS)
      setCampaigns(res.data)
    } catch (err) {
      toast.error("Failed to load campaigns")
    } finally {
      setLoading(false)
    }
  }


  const fetchReportData = useCallback(async (id: string) => {
    setIsLoadingReport(true)
    try {
      const response = await API.get(ENDPOINTS.BULK_COMMUNICATION.REPORT(id))
      setReportData(response.data)
    } catch (error) {
      toast.error("Failed to fetch report metrics")
    } finally {
      setIsLoadingReport(false)
    }
  }, [])

  useEffect(() => {
    if (selectedCampaign) {
      fetchReportData(selectedCampaign.id)
    } else {
      setReportData(null)
    }
  }, [selectedCampaign, fetchReportData])

  // Auto-refresh Detail View
  useEffect(() => {
    let interval: any;
    if (view === 'detail' && selectedCampaign?.id) {
      const isProcessing = selectedCampaign.status === 'sending' || selectedCampaign.status === 'dispatched';
      interval = setInterval(() => {
        fetchReportData(selectedCampaign.id);
        // Also refresh the campaign object itself to update stats/status
        API.get(`${ENDPOINTS.BULK_COMMUNICATION.CAMPAIGNS}${selectedCampaign.id}/`).then(res => {
          setSelectedCampaign(res.data);
        });
      }, isProcessing ? 3000 : 10000); // High frequency polling when active
    }
    return () => clearInterval(interval);
  }, [view, selectedCampaign?.id, selectedCampaign?.status, fetchReportData]);



  const handleDispatch = async (id: string) => {
    try {
      await API.post(ENDPOINTS.BULK_COMMUNICATION.DISPATCH(id))
      toast.success("Campaign dispatched!")
      fetchCampaigns()
      if (selectedCampaign?.id === id) {
        setSelectedCampaign(prev => prev ? { ...prev, status: 'dispatched' } : null)
      }
    } catch (err) {
      toast.error("Failed to dispatch campaign")
    }
  }

  const handlePause = async (id: string) => {
    try {
      await API.post(ENDPOINTS.BULK_COMMUNICATION.PAUSE(id))
      toast.success("Campaign paused")
      fetchCampaigns()
      if (selectedCampaign?.id === id) {
        setSelectedCampaign(prev => prev ? { ...prev, status: 'paused' } : null)
      }
    } catch (err) {
      toast.error("Failed to pause campaign")
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this campaign? This will remove all associated contacts and reports.")) return
    try {
      await API.delete(ENDPOINTS.BULK_COMMUNICATION.DELETE(id))
      toast.success("Campaign deleted")
      fetchCampaigns()
      if (selectedCampaign?.id === id) setSelectedCampaign(null)
    } catch (err) {
      toast.error("Failed to delete campaign")
    }
  }

  const handleEdit = (campaign: BulkCampaign) => {
    setCampaignToEdit(campaign)
    setShowWizard(true)
  }

  const filteredCampaigns = useMemo(() => {
    return campaigns.filter(c => c.name.toLowerCase().includes(search.toLowerCase()))
  }, [campaigns, search])

  // Stats
  const stats = useMemo(() => {
    const total = campaigns.length
    const sent = campaigns.reduce((acc, c) => acc + c.completed_contacts, 0)
    const failed = campaigns.reduce((acc, c) => acc + c.failed_contacts, 0)
    const active = campaigns.filter(c => c.status === 'sending').length
    return { total, sent, failed, active }
  }, [campaigns])

  // Global Stats
  const globalStats = useMemo(() => {
    const totalContacts = campaigns.reduce((acc, c) => acc + c.total_contacts, 0)
    const totalSent = campaigns.reduce((acc, c) => acc + c.completed_contacts, 0)
    const totalFailed = campaigns.reduce((acc, c) => acc + c.failed_contacts, 0)

    const successRate = totalSent + totalFailed > 0
      ? (totalSent / (totalSent + totalFailed)) * 100
      : 100

    const channelCounts = {
      email: campaigns.filter(c => c.campaign_type === 'email').length,
      sms: campaigns.filter(c => c.campaign_type === 'sms').length,
      whatsapp: campaigns.filter(c => c.campaign_type === 'whatsapp').length,
    }

    return { successRate, channelCounts, totalContacts }
  }, [campaigns])

  const filteredLogs = useMemo(() => {
    if (!reportData?.contacts) return []
    return reportData.contacts.filter((c: any) => {
      const matchesSearch = c.identifier.toLowerCase().includes(dashboardSearch.toLowerCase()) ||
        (c.name || '').toLowerCase().includes(dashboardSearch.toLowerCase())
      const matchesFilter = dashboardFilter === 'all' || c.status === dashboardFilter
      return matchesSearch && matchesFilter
    })
  }, [reportData, dashboardSearch, dashboardFilter])

  const handleRecycle = async (id: string) => {
    if (!window.confirm("This will reset all failed contacts to 'queued' status. Proceed?")) return
    try {
      await API.post(ENDPOINTS.BULK_COMMUNICATION.RELAUNCH(id))
      toast.success("Campaign recycled and ready for dispatch!")
      fetchCampaigns()
      if (selectedCampaign?.id === id) {
        setSelectedCampaign(prev => prev ? { ...prev, status: 'active', failed_contacts: 0 } : null)
        fetchReportData(id)
      }
    } catch (err) {
      toast.error("Failed to recycle campaign")
    }
  }

  const typeIcon = (type: CampaignType) => {
    switch (type) {
      case 'email': return <Mail className="h-4 w-4" />
      case 'sms': return <Send className="h-4 w-4" />
      case 'whatsapp': return <MessageSquare className="h-4 w-4" />
    }
  }

  const statusBadge = (status: CampaignStatus) => {
    const configs: Record<CampaignStatus, { color: string, label: string }> = {
      active: { color: "bg-blue-500/10 text-blue-600 border-blue-200", label: "Ready" },
      sending: { color: "bg-indigo-500/10 text-indigo-600 border-indigo-200 animate-pulse", label: "Sending..." },
      dispatched: { color: "bg-emerald-500/10 text-emerald-600 border-emerald-200", label: "Sent" },
      completed: { color: "bg-emerald-500/10 text-emerald-600 border-emerald-200", label: "Completed" },
      paused: { color: "bg-slate-500/10 text-slate-600 border-slate-200", label: "Paused" },
      failed: { color: "bg-rose-500/10 text-rose-600 border-rose-200", label: "Failed" },
    }
    const config = configs[status]
    return <Badge variant="outline" className={cn(config.color, "font-bold uppercase text-[10px]")}>{config.label}</Badge>
  }

  if (view === 'detail' && selectedCampaign) {
    return (
      <div className="p-6 space-y-8 bg-slate-50/50 dark:bg-slate-950/50 min-h-screen">
        {/* Detail Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl p-6 rounded-[2rem] border border-slate-200 dark:border-slate-800 shadow-2xl sticky top-4 z-40">
          <div className="flex items-center gap-5">
            <Button
              variant="ghost"
              size="icon"
              className="h-12 w-12 rounded-xl bg-slate-100 dark:bg-slate-800"
              onClick={() => setView('list')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className={cn(
              "h-16 w-16 rounded-2xl flex items-center justify-center text-white shadow-lg ring-4 ring-indigo-500/10",
              selectedCampaign.campaign_type === 'email' ? "bg-blue-600" :
                selectedCampaign.campaign_type === 'sms' ? "bg-amber-600" : "bg-emerald-600"
            )}>
              {typeIcon(selectedCampaign.campaign_type)}
            </div>
            <div>
              <h1 className="text-2xl font-black tracking-tight text-slate-900 dark:text-white uppercase italic">{selectedCampaign.name}</h1>
              <div className="flex items-center gap-3 mt-1">
                <Badge className="bg-indigo-600/10 text-indigo-500 border-none px-3 py-0.5 font-black uppercase text-[9px] tracking-widest">
                  {selectedCampaign.campaign_type} Stream
                </Badge>
                <div className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Active Tracking</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 p-2 bg-slate-100 dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700">
            {(selectedCampaign.status === 'active' || selectedCampaign.status === 'paused') && (
              <Button
                onClick={() => handleDispatch(selectedCampaign.id)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[10px] tracking-widest px-6 h-12 rounded-xl shadow-lg shadow-emerald-500/20"
              >
                <Play className="h-4 w-4 mr-2" /> {selectedCampaign.status === 'paused' ? 'Resume' : 'Start'} Campaign
              </Button>
            )}
            {selectedCampaign.status === 'dispatched' && (
              <Button
                variant="outline"
                onClick={() => handlePause(selectedCampaign.id)}
                className="border-amber-500/20 bg-amber-500/10 text-amber-600 font-black uppercase text-[10px] tracking-widest px-6 h-12 rounded-xl"
              >
                <Pause className="h-4 w-4 mr-2" /> Pause Stream
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => handleRecycle(selectedCampaign.id)}
              className="border-indigo-500/20 bg-indigo-500/10 text-indigo-600 font-black uppercase text-[10px] tracking-widest px-6 h-12 rounded-xl"
            >
              <RefreshCw className="h-4 w-4 mr-2" /> Recycle
            </Button>
            <Button
              variant="outline"
              onClick={() => fetchReportData(selectedCampaign.id)}
              disabled={isLoadingReport}
              className="border-white/10 bg-white/5 text-slate-400 font-black uppercase text-[10px] tracking-widest px-4 h-12 rounded-xl"
            >
              <RefreshCw className={cn("h-4 w-4", isLoadingReport && "animate-spin")} />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Analytics */}
          <div className="lg:col-span-3 space-y-8">
            {/* Progress Card */}
            <Card className="rounded-[2.5rem] border-none bg-white dark:bg-slate-900 p-10 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 p-12 opacity-[0.03] rotate-12 transition-transform group-hover:rotate-45">
                {typeIcon(selectedCampaign.campaign_type)}
              </div>
              <div className="relative z-10 space-y-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-sm font-black text-slate-400 uppercase tracking-[0.3em] mb-1">Transmission Progress</h2>
                    <p className="text-4xl font-black text-slate-900 dark:text-white tracking-tighter italic">
                      {Math.round((selectedCampaign.completed_contacts / (selectedCampaign.total_contacts || 1)) * 100)}% <span className="text-sm font-bold text-slate-500 uppercase not-italic">Complete</span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-black text-emerald-500 tracking-widest uppercase">Target Reached</p>
                    <p className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">{selectedCampaign.completed_contacts} / {selectedCampaign.total_contacts}</p>
                  </div>
                </div>
                <div className="h-4 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden shadow-inner p-1">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(selectedCampaign.completed_contacts / (selectedCampaign.total_contacts || 1)) * 100}%` }}
                    className="h-full bg-gradient-to-r from-indigo-600 via-violet-600 to-emerald-500 rounded-full shadow-[0_0_20px_rgba(79,70,229,0.4)]"
                  />
                </div>
              </div>
            </Card>

            {/* Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { label: 'Success Rate', value: reportData?.stats?.total > 0 ? `${Math.round((reportData.stats.completed / reportData.stats.total) * 100)}%` : '0%', icon: Zap, color: 'emerald' },
                { label: 'Failure Count', value: reportData?.stats?.failed || 0, icon: AlertCircle, color: 'rose' },
                { label: 'Remaining', value: (reportData?.stats?.total || 0) - (reportData?.stats?.completed || 0) - (reportData?.stats?.failed || 0), icon: Clock, color: 'amber' },
              ].map((m, i) => (
                <div key={i} className="p-8 bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-slate-800 shadow-xl group hover:scale-[1.02] transition-all">
                  <div className={cn(`h-12 w-12 rounded-2xl bg-${m.color}-500/10 text-${m.color}-600 flex items-center justify-center mb-6`)}>
                    <m.icon className="h-6 w-6" />
                  </div>
                  <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">{m.label}</p>
                  <p className="text-3xl font-black text-slate-900 dark:text-white tracking-tighter italic">{m.value}</p>
                </div>
              ))}
            </div>

            {/* Logs Table */}
            <Card className="rounded-[2.5rem] border-none bg-white dark:bg-slate-900 overflow-hidden shadow-2xl">
              <div className="p-8 border-b border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
                <div className="space-y-1">
                  <h3 className="text-sm font-black uppercase tracking-widest text-slate-900 dark:text-white flex items-center gap-3">
                    <Activity className="h-4 w-4 text-indigo-500" />
                    Execution Logs
                  </h3>
                  <p className="text-[10px] font-bold text-slate-400 uppercase">Showing {filteredLogs.length} contacts</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
                    <Input
                      placeholder="Search identifier..."
                      className="pl-8 h-10 w-48 rounded-xl bg-slate-50 dark:bg-slate-800 border-none text-[10px]"
                      value={dashboardSearch}
                      onChange={(e) => setDashboardSearch(e.target.value)}
                    />
                  </div>
                  <Select value={dashboardFilter} onValueChange={setDashboardFilter}>
                    <SelectTrigger className="h-10 w-32 rounded-xl bg-slate-50 dark:bg-slate-800 border-none text-[10px] font-bold uppercase tracking-widest">
                      <SelectValue placeholder="Filter Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="sent">Sent</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                      <SelectItem value="queued">Queued</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Table>
                <TableHeader className="bg-slate-50 dark:bg-slate-800/30">
                  <TableRow>
                    <TableHead className="px-8 font-black text-[10px] uppercase tracking-widest">Identifier</TableHead>
                    <TableHead className="font-black text-[10px] uppercase tracking-widest">Status</TableHead>
                    <TableHead className="font-black text-[10px] uppercase tracking-widest">Timestamp</TableHead>
                    <TableHead className="px-8 font-black text-[10px] uppercase tracking-widest text-right">Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoadingReport && !reportData ? (
                    <TableRow><TableCell colSpan={4} className="h-64 text-center"><Loader2 className="h-10 w-10 animate-spin text-slate-200 mx-auto" /></TableCell></TableRow>
                  ) : filteredLogs.length > 0 ? (
                    filteredLogs.map((contact: any, i: number) => (
                      <TableRow key={i} className="border-b border-slate-50 dark:border-slate-800">
                        <TableCell className="px-8 py-5">
                          <p className="text-sm font-bold text-slate-900 dark:text-white tracking-tight italic">{contact.identifier}</p>
                          <p className="text-[10px] text-slate-500 font-mono">{contact.name || 'Anonymous Recipient'}</p>
                        </TableCell>
                        <TableCell>
                          <Badge className={cn(
                            "px-3 py-1 font-black text-[9px] uppercase tracking-widest border-none",
                            contact.status === 'sent' || contact.status === 'delivered' ? "bg-emerald-500/10 text-emerald-600" :
                              contact.status === 'failed' ? "bg-rose-500/10 text-rose-600" : "bg-slate-500/10 text-slate-500"
                          )}>
                            {contact.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs font-mono text-slate-400">
                          {contact.sent_at ? new Date(contact.sent_at).toLocaleTimeString() : '--:--:--'}
                        </TableCell>
                        <TableCell className="px-8 text-right text-[10px] font-bold text-rose-500/70 uppercase">
                          {contact.error_message || '--'}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow><TableCell colSpan={4} className="h-64 text-center text-slate-400 italic">No activity logged yet</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Card>
          </div>

          {/* Side Panels */}
          <div className="space-y-8">
            <Card className="rounded-[2.5rem] border-none bg-white dark:bg-slate-900 p-8 shadow-2xl space-y-6">
              <h3 className="text-xs font-black uppercase tracking-widest text-indigo-500">Payload Overview</h3>
              {selectedCampaign.subject && (
                <div className="space-y-2">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Subject Line</p>
                  <p className="text-xs font-bold text-slate-900 dark:text-white leading-tight">{selectedCampaign.subject}</p>
                </div>
              )}
              <div className="space-y-2">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Message Content</p>
                <div className="p-6 bg-slate-50 dark:bg-slate-950/50 rounded-2xl text-[11px] font-medium text-slate-500 dark:text-slate-400 leading-relaxed italic border border-slate-100 dark:border-white/5">
                  "{selectedCampaign.body}"
                </div>
              </div>
            </Card>

            <Card className="rounded-[2.5rem] border-none bg-white dark:bg-slate-900 p-8 shadow-2xl">
              <h3 className="text-xs font-black uppercase tracking-widest text-emerald-500 mb-6">Channel Health</h3>
              <div className="space-y-6">
                <div className="flex items-center gap-6">
                  <div className="relative h-20 w-20 flex items-center justify-center">
                    <svg className="h-full w-full transform -rotate-90">
                      <circle cx="40" cy="40" r="34" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-slate-100 dark:text-slate-800" />
                      <circle cx="40" cy="40" r="34" stroke="currentColor" strokeWidth="8" fill="transparent"
                        strokeDasharray={213}
                        strokeDashoffset={213 - (213 * (reportData?.stats?.total > 0 ? (reportData.stats.completed / reportData.stats.total) * 100 : 0)) / 100}
                        className="text-emerald-500"
                      />
                    </svg>
                    <span className="absolute text-lg font-black text-slate-900 dark:text-white">
                      {reportData?.stats?.total > 0 ? Math.round((reportData.stats.completed / reportData.stats.total) * 100) : 0}%
                    </span>
                  </div>
                  <div className="space-y-1">
                    <p className="text-lg font-black text-slate-900 dark:text-white italic">Premium</p>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-tight">Delivery Efficiency</p>
                  </div>
                </div>
                <div className="pt-6 border-t border-slate-100 dark:border-slate-800">
                  <Button className="w-full h-14 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white font-black uppercase text-[10px] tracking-widest shadow-xl shadow-indigo-600/30">
                    <Download className="h-4 w-4 mr-2" /> Export Performance
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-8 bg-slate-50/50 dark:bg-slate-950/50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl p-6 rounded-[2rem] border border-slate-200 dark:border-slate-800 shadow-2xl shadow-slate-200/50 dark:shadow-none sticky top-4 z-40">
        <div className="flex items-center gap-5">
          <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center text-white shadow-lg shadow-indigo-500/30 ring-4 ring-indigo-500/10">
            <Send className="h-8 w-8" />
          </div>
          <div>
            <h1 className="text-3xl font-black tracking-tight text-slate-900 dark:text-white">Bulk Communication</h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="flex h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-600 dark:text-emerald-400">Engine Online</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden lg:flex items-center gap-3 px-5 py-3 bg-slate-100 dark:bg-slate-800/50 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-inner">
            <Clock className="h-5 w-5 text-indigo-500" />
            <div className="flex flex-col">
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Session Time</span>
              <span className="text-sm font-mono font-bold text-slate-700 dark:text-slate-300">
                {new Date().toLocaleTimeString()}
              </span>
            </div>
          </div>
          <Button
            onClick={() => setShowWizard(true)}
            className="h-14 px-8 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white rounded-2xl font-bold shadow-lg shadow-indigo-500/25 flex items-center gap-2 transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            <Plus className="h-5 w-5" />
            New Campaign
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { label: "Total Campaigns", val: stats.total, icon: Layers, color: "indigo", sub: "All channels" },
          { label: "Active Streams", val: stats.active, icon: Activity, color: "amber", sub: "Currently processing" },
          { label: "Total Dispatched", val: stats.sent.toLocaleString(), icon: CheckCircle2, color: "emerald", sub: "Successfully sent" },
          { label: "Failed Delivery", val: stats.failed, icon: AlertCircle, color: "rose", sub: "Bounce / Error" },
        ].map((s, idx) => (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05, duration: 0.2 }}
            key={s.label}
            className="group relative bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden"
          >
            <div className={`absolute top-0 right-0 w-32 h-32 bg-${s.color}-500/5 rounded-full -mr-16 -mt-16 blur-2xl group-hover:bg-${s.color}-500/10 transition-all`} />
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-2xl bg-${s.color}-500/10 text-${s.color}-600 dark:text-${s.color}-400`}>
                <s.icon className="h-6 w-6" />
              </div>
              <Badge variant="secondary" className="bg-slate-100 dark:bg-slate-800 font-mono">Live</Badge>
            </div>
            <div className="space-y-1">
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">{s.label}</p>
              <h3 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">{s.val}</h3>
              <p className="text-[10px] text-slate-400 font-medium">{s.sub}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Campaign List */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="rounded-[2.5rem] border-slate-200 dark:border-slate-800 overflow-hidden shadow-xl shadow-slate-200/20 dark:shadow-none">
            <CardHeader className="bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800 px-8 py-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <CardTitle className="text-xl font-bold flex items-center gap-3">
                  <BarChart3 className="h-5 w-5 text-indigo-500" />
                  Recent Campaigns
                </CardTitle>
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search campaigns..."
                    className="pl-10 h-10 rounded-xl bg-slate-50 dark:bg-slate-800 border-none focus-visible:ring-2 focus-visible:ring-indigo-500/20"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[600px]">
                <Table>
                  <TableHeader className="bg-slate-50/50 dark:bg-slate-800/30 sticky top-0 z-10">
                    <TableRow>
                      <TableHead className="px-8 font-bold text-[11px] uppercase tracking-wider text-slate-400">Campaign Details</TableHead>
                      <TableHead className="font-bold text-[11px] uppercase tracking-wider text-slate-400">Progress</TableHead>
                      <TableHead className="font-bold text-[11px] uppercase tracking-wider text-slate-400">Status</TableHead>
                      <TableHead className="font-bold text-[11px] uppercase tracking-wider text-slate-400">Stats</TableHead>
                      <TableHead className="px-8 font-bold text-[11px] uppercase tracking-wider text-slate-400 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCampaigns.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="h-64 text-center">
                          <div className="flex flex-col items-center gap-3 text-slate-400">
                            <Layers className="h-12 w-12 opacity-20" />
                            <p className="font-medium">No campaigns found</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredCampaigns.map((c) => (
                        <TableRow
                          key={c.id}
                          className="group hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer border-b border-slate-100 dark:border-slate-800"
                          onClick={() => {
                            setSelectedCampaign(c)
                            setView('detail')
                            fetchReportData(c.id)
                          }}
                        >
                          <TableCell className="px-8 py-5">
                            <div className="flex items-center gap-4">
                              <div className={cn(
                                "h-12 w-12 rounded-xl flex items-center justify-center shadow-inner",
                                c.campaign_type === 'email' ? "bg-blue-500/10 text-blue-600" :
                                  c.campaign_type === 'sms' ? "bg-amber-500/10 text-amber-600" : "bg-emerald-500/10 text-emerald-600"
                              )}>
                                {typeIcon(c.campaign_type)}
                              </div>
                              <div>
                                <p className="font-bold text-slate-900 dark:text-white">{c.name}</p>
                                <p className="text-[10px] text-slate-500 font-mono mt-0.5">{new Date(c.created_at).toLocaleDateString()}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="w-32 space-y-1.5">
                              <div className="flex items-center justify-between text-[10px] font-bold">
                                <span className="text-slate-400">Progress</span>
                                <span className="text-indigo-600">
                                  {Math.round((c.completed_contacts / (c.total_contacts || 1)) * 100)}%
                                </span>
                              </div>
                              <Progress
                                value={(c.completed_contacts / (c.total_contacts || 1)) * 100}
                                className={cn(
                                  "h-1.5 bg-slate-100 dark:bg-slate-800",
                                  (c.status === 'sending' || c.status === 'dispatched') && "after:content-[''] after:absolute after:inset-0 after:bg-gradient-to-r after:from-transparent after:via-white/20 after:to-transparent after:animate-[shimmer_2s_infinite]"
                                )}
                              />
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className={c.status === 'sending' ? 'animate-pulse' : ''}>
                              {statusBadge(c.status)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-2">
                                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300">{c.completed_contacts}</span>
                                <span className="text-[9px] text-slate-400 uppercase font-bold">Sent</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
                                <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300">{c.failed_contacts}</span>
                                <span className="text-[9px] text-slate-400 uppercase font-bold">Failed</span>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="px-8 text-right">
                            <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              {c.status === 'active' && (
                                <>
                                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 rounded-lg bg-emerald-500/10 text-emerald-600" onClick={(e) => {
                                    e.stopPropagation()
                                    handleDispatch(c.id)
                                  }}>
                                    <Play className="h-4 w-4" />
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 rounded-lg bg-indigo-500/10 text-indigo-600" onClick={(e) => {
                                    e.stopPropagation()
                                    handleEdit(c)
                                  }}>
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 rounded-lg bg-rose-500/10 text-rose-600" onClick={(e) => {
                                e.stopPropagation()
                                handleDelete(c.id)
                              }}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">
                                <Download className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Live Report / Context Area */}
        <div className="space-y-6">
          <Card className="rounded-[2.5rem] border-slate-200 dark:border-slate-800 shadow-xl p-8 bg-gradient-to-br from-indigo-600 to-violet-700 text-white border-none overflow-hidden relative">
            <Zap className="absolute -top-6 -right-6 h-32 w-32 text-white/10" />
            <div className="relative z-10 space-y-6">
              <div className="flex items-center justify-between">
                <Badge className="bg-white/20 text-white border-none backdrop-blur-md">GLOBAL PERFORMANCE</Badge>
                <div className="flex h-3 w-3 rounded-full bg-emerald-400 animate-pulse ring-4 ring-emerald-400/20" />
              </div>
              <div className="space-y-1">
                <h2 className="text-4xl font-black tracking-tighter italic">
                  {globalStats.successRate.toFixed(1)}%
                </h2>
                <p className="text-sm font-bold text-white/70 uppercase tracking-widest">Success Achievement</p>
              </div>
              <div className="pt-4 space-y-4">
                <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-white/60">
                  <span>Channel Breakdown</span>
                </div>
                <div className="space-y-2">
                  {[
                    { label: 'Email', count: globalStats.channelCounts.email, color: 'bg-blue-400' },
                    { label: 'SMS', count: globalStats.channelCounts.sms, color: 'bg-amber-400' },
                    { label: 'WhatsApp', count: globalStats.channelCounts.whatsapp, color: 'bg-emerald-400' },
                  ].map(ch => (
                    <div key={ch.label} className="space-y-1">
                      <div className="flex justify-between text-[9px] font-black uppercase">
                        <span>{ch.label}</span>
                        <span>{ch.count}</span>
                      </div>
                      <Progress
                        value={stats.total > 0 ? (ch.count / stats.total) * 100 : 0}
                        className="h-1 bg-white/10"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Card>

          <Card className="rounded-[2.5rem] border-slate-200 dark:border-slate-800 shadow-lg p-6 bg-white dark:bg-slate-900">
            <h3 className="font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2">
              <Flag className="h-4 w-4 text-indigo-500" />
              Campaign Performance
            </h3>
            {selectedCampaign ? (
              <div className="space-y-6">
                <div className="space-y-2">
                  <div className="flex justify-between text-xs font-bold uppercase tracking-wider text-slate-400">
                    <span>Reach Achievement</span>
                    <span className="text-indigo-600">{Math.round((selectedCampaign.completed_contacts / (selectedCampaign.total_contacts || 1)) * 100)}%</span>
                  </div>
                  <Progress value={(selectedCampaign.completed_contacts / (selectedCampaign.total_contacts || 1)) * 100} className="h-3 bg-slate-100 dark:bg-slate-800" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800">
                    <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Delivered</p>
                    <p className="text-xl font-black text-emerald-600">{selectedCampaign.completed_contacts}</p>
                  </div>
                  <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800">
                    <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Failed</p>
                    <p className="text-xl font-black text-rose-600">{selectedCampaign.failed_contacts}</p>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
                  <p className="text-xs font-bold text-slate-900 dark:text-white mb-3 uppercase tracking-wider">Top Failures</p>
                  {isLoadingReport ? (
                    <div className="flex items-center justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div>
                  ) : reportData?.contacts?.filter((c: any) => c.status === 'failed').length > 0 ? (
                    <div className="space-y-2">
                      {reportData.contacts.filter((c: any) => c.status === 'failed').slice(0, 3).map((c: any, i: number) => (
                        <div key={i} className="flex items-center gap-3 p-2 bg-rose-500/5 rounded-xl text-[10px]">
                          <AlertCircle className="h-3 w-3 text-rose-500 shrink-0" />
                          <span className="font-mono text-slate-600 dark:text-slate-400 truncate">{c.identifier}</span>
                          <span className="text-rose-600 font-bold ml-auto truncate">{c.error_message || "Bounce"}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[10px] text-slate-400 italic">No failed contacts reported for this campaign.</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center space-y-4">
                <BarChart3 className="h-12 w-12 text-slate-200" />
                <p className="text-sm font-medium text-slate-400">Select a campaign to view detailed performance metrics</p>
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* High-Fidelity Campaign Creator */}
      <CampaignWizard
        isOpen={showWizard}
        onClose={() => {
          setShowWizard(false)
          setCampaignToEdit(null)
          if (location.state?.selectedContacts) {
            navigate(location.pathname, { replace: true, state: {} });
          }
        }}
        onSuccess={fetchCampaigns}
        initialData={campaignToEdit}
        preSelectedContacts={location.state?.selectedContacts}
        initialType={location.state?.initialType}
      />
    </div>
  )
}

// --- Subcomponents ---

interface CampaignWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialData?: BulkCampaign | null;
  preSelectedContacts?: any[];
  initialType?: string;
}

const CampaignWizard = ({ isOpen, onClose, onSuccess, initialData, preSelectedContacts, initialType }: CampaignWizardProps) => {
  const [wizardStep, setWizardStep] = useState(1)
  const [isCreating, setIsCreating] = useState(false)
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [newCampaign, setNewCampaign] = useState({
    name: "",
    type: "email" as CampaignType,
    subject: "",
    body: "",
    template_id: "1707177771006280771",
    entity_id: "1701177736801468554",
    whatsapp_template_name: "nlec_lpu",
    image_url: "",
    sms_template: "lpu",
    wa_template: "nlec_lpu"
  })

  const SMS_TEMPLATES = [
    {
      id: 'lpu',
      name: 'NLEC LPU',
      template_id: '1707177806581052308',
      entity_id: '1701177736801468554',
      body: ""
    },
    {
      id: 'shu',
      name: 'NLEC SHU',
      template_id: '1707177797988457112',
      entity_id: '1701177736801468554',
      body: ""
    }
  ]

  const WHATSAPP_TEMPLATES = [
    {
      id: 'nlec_lpu',
      name: 'NLEC LPU (WA)',
      template_id: '3229351',
      body: `Phase 1 admissions are now closed at Lovely Professional University.

Applications are open for the next phase. Limited scholarship seats are available for eligible applicants.

*Why LPU:*

*Industry-aligned curriculum with practical learning
*Internship and “earn while you learn” opportunities
*Global exposure with diverse student community
*Strong placement ecosystem with leading recruiters
*Scholarships available up to 100%

Students benefit from career-focused training designed to improve employability and real-world skills.

Limited scholarship seats remain. Apply now to secure your eligibility for up to 100% scholarship.

Proceed here: https://vm.ltd/LPU/E0Sq3G

For assistance, contact: 7837001103`
    },
    {
      id: 'nlec_shu',
      name: 'NLEC SHU (WA)',
      template_id: '3229354',
      body: "Phase 1 Admissions closed at Shoolini University.Apply now to secure up to 100% scholarship: https://vm-.ltd/SHU/FPOSVS Call: 7837001103"
    }
  ]

  const [parsedContacts, setParsedContacts] = useState<any[]>([])

  useEffect(() => {
    if (isOpen && initialData) {
      setNewCampaign({
        name: initialData.name,
        type: initialData.campaign_type,
        subject: initialData.subject || "",
        body: initialData.body || "",
        template_id: initialData.template_id || "",
        entity_id: initialData.entity_id || "",
        whatsapp_template_name: initialData.whatsapp_template_name || "",
        image_url: initialData.image_url || "",
        sms_template: initialData.body.toLowerCase().includes('shu') ? 'shu' : 'lpu',
        wa_template: initialData.whatsapp_template_name?.includes('shu') ? 'nlec_shu' : 'nlec_lpu'
      })
      setWizardStep(1)
    } else if (isOpen) {
      resetWizard()
      if (initialType) {
        setNewCampaign(prev => ({ ...prev, type: initialType as CampaignType }))
      }
    }
  }, [isOpen, initialData, initialType])

  const resetWizard = () => {
    setWizardStep(1)
    setNewCampaign({
      name: "",
      type: "email" as CampaignType,
      subject: "",
      body: "",
      template_id: "",
      entity_id: "",
      whatsapp_template_name: "",
      image_url: "",
      sms_template: "lpu",
      wa_template: "nlec_lpu"
    })
    setCsvFile(null)
    setParsedContacts([])
  }

  const parseCSV = (file: File) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const text = e.target?.result as string
      if (!text) return
      const lines = text.split('\n').filter(l => l.trim())
      if (lines.length < 2) return

      const rows = lines.slice(1, 6).map(line => {
        const parts = line.split(',')
        return { identifier: parts[0]?.trim(), name: parts[1]?.trim() }
      })
      setParsedContacts(rows)
    }
    reader.readAsText(file)
  }

  useEffect(() => {
    if (csvFile) parseCSV(csvFile)
    else setParsedContacts([])
  }, [csvFile])

  const handleCreateCampaign = async () => {
    if (!newCampaign.name.trim()) return toast.error("Campaign name is required")
    if (!initialData?.id && !csvFile && (!preSelectedContacts || preSelectedContacts.length === 0)) return toast.error("Recipient list is missing. Please go back to Phase 03.")

    setIsCreating(true)
    try {
      let campaign;
      const payload: any = {
        name: newCampaign.name,
        campaign_type: newCampaign.type,
        subject: newCampaign.subject,
        body: newCampaign.body || "",
        template_id: newCampaign.template_id || null,
        entity_id: newCampaign.entity_id || null,
        whatsapp_template_name: newCampaign.whatsapp_template_name || null,
        image_url: newCampaign.image_url || null
      }

      if (preSelectedContacts && preSelectedContacts.length > 0) {
        payload.contact_ids = preSelectedContacts.map((c: any) => c.id);
        const res = await API.post(ENDPOINTS.BULK_COMMUNICATION.CREATE_FROM_DATA, payload);
        toast.success("Campaign created successfully!");
        await onSuccess()
        resetWizard()
        onClose()
        setIsCreating(false)
        return;
      }

      if (initialData?.id) {
        const res = await API.put(ENDPOINTS.BULK_COMMUNICATION.CAMPAIGNS + initialData.id + "/", payload)
        campaign = res.data
      } else {
        const res = await API.post(ENDPOINTS.BULK_COMMUNICATION.CAMPAIGNS, payload)
        campaign = res.data
      }

      if (csvFile) {
        const formData = new FormData()
        formData.append('file', csvFile)
        await API.post(ENDPOINTS.BULK_COMMUNICATION.UPLOAD_CSV(campaign.id), formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
      }

      toast.success(initialData?.id ? "Campaign updated successfully!" : "Campaign created successfully!")

      // Small delay to allow DB commit to settle before refreshing
      setTimeout(async () => {
        await onSuccess()
        resetWizard()
        onClose()
      }, 500);
    } catch (err) {
      toast.error(initialData?.id ? "Failed to update campaign" : "Failed to create campaign")
    } finally {
      setIsCreating(false)
    }
  }

  const canProceed = useMemo(() => {
    if (wizardStep === 1) return !!newCampaign.name.trim()
    if (wizardStep === 2) return true
    if (wizardStep === 3) return !!csvFile || (preSelectedContacts && preSelectedContacts.length > 0)
    if (wizardStep === 4) return true
    return true
  }, [wizardStep, newCampaign, csvFile, preSelectedContacts])

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:!max-w-[1550px] w-[98vw] h-[90vh] p-0 overflow-hidden rounded-[2.5rem] border-none shadow-[0_0_120px_-15px_rgba(0,0,0,0.6)] bg-slate-950 flex flex-col">
        <div className="flex h-full w-full relative">
          <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] bg-indigo-600/10 blur-[120px] rounded-full" />
          <div className="absolute bottom-[-10%] left-[20%] w-[400px] h-[400px] bg-blue-600/10 blur-[100px] rounded-full" />

          <div className="w-[280px] bg-slate-900/40 backdrop-blur-2xl border-r border-white/5 p-10 flex flex-col justify-between shrink-0 z-10">
            <div className="space-y-10">
              <div className="space-y-2">
                <Badge className="bg-indigo-600/20 text-indigo-400 border-none px-2.5 py-1 text-[9px] font-bold tracking-[0.2em] uppercase">Campaign Lab</Badge>
                <h3 className="text-2xl font-black text-white tracking-tighter leading-none">{initialData?.id ? "Edit" : "New"} <br />Campaign</h3>
                <p className="text-[10px] font-medium text-slate-400/80 leading-relaxed max-w-[180px]">Architecting communication streams at scale.</p>
              </div>

              <div className="space-y-6 relative">
                <div className="absolute left-5 top-3 bottom-3 w-px bg-white/10" />
                {STEPS.map((step) => (
                  <div key={step.id} className="flex items-center gap-4 relative group">
                    <div className={cn(
                      "h-10 w-10 rounded-xl flex items-center justify-center transition-all duration-500 z-10",
                      wizardStep === step.id ? "bg-indigo-600 text-white shadow-[0_0_20px_rgba(79,70,229,0.4)] scale-110" :
                        wizardStep > step.id ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" : "bg-slate-800/50 text-slate-500 border border-white/5"
                    )}>
                      {wizardStep > step.id ? <CheckCircle2 className="h-4 w-4" /> : <step.icon className="h-4 w-4" />}
                    </div>
                    <div className="flex flex-col">
                      <span className={cn("text-[8px] font-black uppercase tracking-[0.2em]", wizardStep === step.id ? "text-indigo-400" : wizardStep > step.id ? "text-emerald-400" : "text-slate-500")}>Phase 0{step.id}</span>
                      <span className={cn("text-sm font-bold tracking-tight", wizardStep === step.id ? "text-white" : "text-slate-400")}>{step.label}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-6 bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-2xl border border-white/5">
              <Zap className="h-5 w-5 text-indigo-400 mb-3" />
              <p className="text-[10px] font-medium text-slate-300 leading-relaxed">
                <span className="text-white font-bold">Pro Tip:</span> Use <code className="bg-white/10 px-1 py-0.5 rounded text-indigo-300">{"{{name}}"}</code> to personalize.
              </p>
            </div>
          </div>

          <div className="flex-1 flex flex-col bg-slate-950/40 relative z-10 overflow-hidden h-full min-h-0">
            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-800 scrollbar-track-transparent min-h-0">
              <div className="max-w-[1100px] mx-auto px-12 py-10 pb-20">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={wizardStep}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="space-y-12"
                  >
                    {wizardStep === 1 && (
                      <div className="space-y-8">
                        <div className="space-y-4">
                          <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Campaign Identity</label>
                          <Input
                            placeholder="e.g. Summer 2026 Promotional Blast"
                            className="h-16 rounded-2xl bg-slate-900/50 border-white/5 text-xl font-bold px-8 text-white shadow-2xl focus:ring-indigo-500/20"
                            value={newCampaign.name}
                            onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                          />
                        </div>
                        <div className="space-y-6">
                          <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Select Protocol</label>
                          <div className="grid grid-cols-3 gap-4">
                            {[
                              { id: 'email', label: 'Email', icon: Mail, color: 'blue' },
                              { id: 'sms', label: 'SMS', icon: Send, color: 'amber' },
                              { id: 'whatsapp', label: 'WhatsApp', icon: MessageSquare, color: 'emerald' },
                            ].map((t) => (
                              <button
                                key={t.id}
                                onClick={() => {
                                  const updates: any = { type: t.id as CampaignType };
                                  if (t.id === 'sms') {
                                    updates.template_id = "1707177771006280771";
                                    updates.entity_id = "1701177736801468554";
                                    updates.sms_template = "lpu";
                                    updates.body = "";
                                  } else if (t.id === 'whatsapp') {
                                    updates.whatsapp_template_name = "nlec_lpu";
                                    updates.wa_template = "nlec_lpu";
                                    updates.body = WHATSAPP_TEMPLATES[0].body;
                                    updates.image_url = "";
                                  } else {
                                    updates.template_id = "";
                                    updates.entity_id = "";
                                  }
                                  setNewCampaign({ ...newCampaign, ...updates });
                                }}
                                className={cn(
                                  "flex flex-col items-center gap-4 p-6 rounded-3xl border-2 transition-all duration-500",
                                  newCampaign.type === t.id ? `border-${t.color}-500 bg-${t.color}-500/10 shadow-2xl` : "border-white/5 bg-slate-900/30 hover:border-white/10"
                                )}
                              >
                                <div className={cn("h-12 w-12 rounded-xl flex items-center justify-center transition-all", newCampaign.type === t.id ? `bg-${t.color}-500 text-white` : "bg-slate-800 text-slate-500")}>
                                  <t.icon className="h-6 w-6" />
                                </div>
                                <span className={cn("font-black text-[10px] uppercase tracking-[0.2em]", newCampaign.type === t.id ? `text-${t.color}-400` : "text-slate-500")}>{t.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* SMS Template Selection */}
                        {newCampaign.type === 'sms' && (
                          <div className="space-y-4 p-6 bg-amber-500/5 rounded-3xl border border-amber-500/10">
                            <label className="text-[10px] font-black uppercase tracking-widest text-amber-500">Select SMS Template</label>
                            <div className="grid grid-cols-2 gap-4">
                              {SMS_TEMPLATES.map((t) => (
                                <button
                                  key={t.id}
                                  onClick={() => setNewCampaign({
                                    ...newCampaign,
                                    sms_template: t.id,
                                    body: t.body,
                                    template_id: t.template_id,
                                    entity_id: t.entity_id
                                  })}
                                  className={cn(
                                    "flex items-center justify-between p-4 rounded-2xl border-2 transition-all",
                                    newCampaign.sms_template === t.id ? "border-amber-500 bg-amber-500/10" : "border-white/5 bg-slate-900/30 hover:border-white/10"
                                  )}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className={cn("h-8 w-8 rounded-lg flex items-center justify-center", newCampaign.sms_template === t.id ? "bg-amber-500 text-white" : "bg-slate-800 text-slate-500")}>
                                      <Flag className="h-4 w-4" />
                                    </div>
                                    <span className={cn("font-bold text-xs", newCampaign.sms_template === t.id ? "text-white" : "text-slate-400")}>{t.name}</span>
                                  </div>
                                  {newCampaign.sms_template === t.id && <CheckCircle2 className="h-4 w-4 text-amber-500" />}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* WhatsApp Template Selection */}
                        {newCampaign.type === 'whatsapp' && (
                          <div className="space-y-6">
                            <div className="space-y-4 p-6 bg-emerald-500/5 rounded-3xl border border-emerald-500/10">
                              <label className="text-[10px] font-black uppercase tracking-widest text-emerald-500">Select WhatsApp Template</label>
                              <div className="grid grid-cols-2 gap-4">
                                {WHATSAPP_TEMPLATES.map((t) => (
                                  <button
                                    key={t.id}
                                    onClick={() => setNewCampaign({
                                      ...newCampaign,
                                      wa_template: t.id,
                                      body: t.body,
                                      whatsapp_template_name: t.template_id
                                    })}
                                    className={cn(
                                      "flex items-center justify-between p-4 rounded-2xl border-2 transition-all",
                                      newCampaign.wa_template === t.id ? "border-emerald-500 bg-emerald-500/10" : "border-white/5 bg-slate-900/30 hover:border-white/10"
                                    )}
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className={cn("h-8 w-8 rounded-lg flex items-center justify-center", newCampaign.wa_template === t.id ? "bg-emerald-500 text-white" : "bg-slate-800 text-slate-500")}>
                                        <MessageSquare className="h-4 w-4" />
                                      </div>
                                      <span className={cn("font-bold text-xs", newCampaign.wa_template === t.id ? "text-white" : "text-slate-400")}>{t.name}</span>
                                    </div>
                                    {newCampaign.wa_template === t.id && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                                  </button>
                                ))}
                              </div>
                            </div>

                          </div>
                        )}
                      </div>
                    )}

                    {wizardStep === 2 && (
                      <div className="space-y-8">
                        {newCampaign.type === 'email' && (
                          <div className="space-y-4">
                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Email Headline</label>
                            <Input
                              placeholder="Subject line..."
                              className="h-14 rounded-2xl bg-slate-900/50 border-white/5 text-base font-bold px-6 text-white focus:ring-indigo-500/20"
                              value={newCampaign.subject}
                              onChange={(e) => setNewCampaign({ ...newCampaign, subject: e.target.value })}
                            />
                          </div>
                        )}
                        
                        {newCampaign.type === 'whatsapp' && (
                          <div className="space-y-4 p-6 bg-indigo-500/5 rounded-3xl border border-indigo-500/10">
                            <div className="flex items-center justify-between">
                              <label className="text-[10px] font-black uppercase tracking-widest text-indigo-400 flex items-center gap-2">
                                <Plus className="h-3 w-3" /> WhatsApp Media Attachment (Image URL)
                              </label>
                              <div className="relative">
                                <input
                                  type="file"
                                  id="wizard-media-upload"
                                  className="hidden"
                                  accept="image/*"
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    
                                    const formData = new FormData();
                                    formData.append("file", file);
                                    
                                    const uploadToast = toast.loading("Uploading media...");
                                    try {
                                      const response = await API.post(ENDPOINTS.BULK_COMMUNICATION.UPLOAD_MEDIA, formData, {
                                        headers: { "Content-Type": "multipart/form-data" }
                                      });
                                      if (response.data.url) {
                                        setNewCampaign(prev => ({ ...prev, image_url: response.data.url }));
                                        toast.success("Media uploaded successfully", { id: uploadToast });
                                      }
                                    } catch (err) {
                                      toast.error("Failed to upload media", { id: uploadToast });
                                    }
                                  }}
                                />
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="h-7 text-[10px] gap-1.5 border-indigo-500/30 text-indigo-400 hover:bg-indigo-500/10"
                                  onClick={() => document.getElementById("wizard-media-upload")?.click()}
                                >
                                  <Upload className="h-3 w-3" />
                                  Upload Image
                                </Button>
                              </div>
                            </div>
                            <Input
                              placeholder="https://example.com/image.jpg"
                              className="h-12 rounded-xl bg-slate-900/50 border-white/5 text-sm font-medium px-6 text-white focus:ring-indigo-500/20"
                              value={newCampaign.image_url}
                              onChange={(e) => setNewCampaign(prev => ({ ...prev, image_url: e.target.value }))}
                            />
                            <p className="text-[9px] text-slate-500 italic">Optional: Provide a public URL for the image you want to attach to this message.</p>
                          </div>
                        )}

                        <div className="space-y-4">
                          <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Core Content</label>
                          <textarea
                            className={cn(
                              "w-full min-h-[400px] rounded-[2.5rem] bg-slate-900/50 border border-white/5 p-10 text-base font-medium text-white resize-none shadow-inner focus:ring-indigo-500/20 leading-relaxed",
                              (newCampaign.type === 'sms' || newCampaign.type === 'whatsapp') && "opacity-50 cursor-not-allowed"
                            )}
                            placeholder="Compose your narrative..."
                            value={newCampaign.body}
                            readOnly={newCampaign.type === 'sms' || newCampaign.type === 'whatsapp'}
                            onChange={(e) => setNewCampaign({ ...newCampaign, body: e.target.value })}
                          />
                        </div>
                      </div>
                    )}

                    {wizardStep === 3 && (
                      <div className="space-y-8">
                        <div className="space-y-6">
                          <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Recipient Dataset</label>
                          {preSelectedContacts && preSelectedContacts.length > 0 ? (
                            <div className="h-[300px] border-2 border-dashed rounded-[2.5rem] flex flex-col items-center justify-center gap-6 transition-all duration-500 relative border-emerald-500/50 bg-emerald-500/10 shadow-2xl">
                              <Users className="h-16 w-16 text-emerald-500" />
                              <div className="text-center space-y-1">
                                <p className="text-xl font-black text-white">Pre-Selected from CRM</p>
                                <p className="text-[9px] font-bold text-emerald-400 uppercase tracking-[0.2em]">{preSelectedContacts.length} Contacts Selected</p>
                              </div>
                            </div>
                          ) : (
                            <div className={cn(
                              "h-[300px] border-2 border-dashed rounded-[2.5rem] flex flex-col items-center justify-center gap-6 transition-all duration-500 relative",
                              csvFile ? "border-emerald-500/50 bg-emerald-500/10 shadow-2xl" : "border-white/10 bg-slate-900/30 hover:border-indigo-500/30"
                            )}>
                              <Upload className={cn("h-12 w-12 transition-all", csvFile ? "text-emerald-500" : "text-slate-500")} />
                              <div className="text-center space-y-1">
                                <p className="text-xl font-black text-white">{csvFile ? csvFile.name : "Ingest Target List"}</p>
                                <p className="text-[9px] font-bold text-slate-500 uppercase tracking-[0.2em]">Drop CSV here</p>
                              </div>
                              <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" accept=".csv" onChange={(e) => setCsvFile(e.target.files?.[0] || null)} />
                              {csvFile && <Button variant="ghost" className="text-rose-500 uppercase font-black text-[9px]" onClick={(e) => { e.stopPropagation(); setCsvFile(null); }}>Purge</Button>}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {wizardStep === 4 && (
                      <div className="space-y-8">
                        <div className="space-y-4 text-center py-4">
                          <div className="h-16 w-16 bg-emerald-500 text-white rounded-2xl flex items-center justify-center mx-auto shadow-lg mb-4">
                            <CheckCircle2 className="h-8 w-8" />
                          </div>
                          <h2 className="text-3xl font-black text-white tracking-tighter leading-tight italic">Mission Ready</h2>
                          <p className="text-slate-500 font-bold uppercase tracking-[0.2em] text-[8px]">Operational integrity confirmed</p>
                        </div>

                        <div className="grid grid-cols-2 gap-6">
                          <div className="p-8 bg-slate-900/50 rounded-[2rem] border border-white/5 space-y-4 relative overflow-hidden group">
                            <div className="absolute top-0 right-0 p-6 opacity-[0.03]">
                              <Settings className="h-24 w-24" />
                            </div>
                            <div className="space-y-4 relative z-10">
                              <div className="flex items-center justify-between">
                                <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest">Config</p>
                                <Badge className="bg-indigo-600 rounded-lg border-none px-2 py-0.5 text-[8px]">{newCampaign.type}</Badge>
                              </div>
                              <h4 className="text-xl font-black text-white tracking-tight truncate">{newCampaign.name}</h4>
                              <div className="pt-4 border-t border-white/5 space-y-2">
                                <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest">Stream</p>
                                <div className="flex items-center gap-3">
                                  <FileText className="h-4 w-4 text-emerald-400" />
                                  <div className="flex flex-col">
                                    <p className="text-xs font-bold text-slate-200 truncate">{preSelectedContacts && preSelectedContacts.length > 0 ? "CRM Leads" : csvFile?.name}</p>
                                    <p className="text-[9px] font-black text-emerald-500 uppercase">{(preSelectedContacts && preSelectedContacts.length > 0) || parsedContacts.length > 0 ? "Data Verified" : "Analyzing..."}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="p-8 bg-slate-900/50 rounded-[2rem] border border-white/5 relative overflow-hidden group">
                            <div className="absolute top-0 right-0 p-6 opacity-[0.03]">
                              <Users className="h-24 w-24" />
                            </div>
                            <div className="space-y-4 relative z-10 h-full flex flex-col">
                              <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest mb-4">Recipient Snippet</p>
                              <div className="space-y-2 flex-1 overflow-y-auto pr-2 max-h-[220px] scrollbar-thin scrollbar-thumb-slate-800 scrollbar-track-transparent">
                                {preSelectedContacts && preSelectedContacts.length > 0 ? (
                                  preSelectedContacts.slice(0, 15).map((c: any, i: number) => (
                                    <div key={i} className="flex items-center justify-between p-3 bg-slate-950/50 rounded-xl border border-white/5 group/item hover:border-indigo-500/30 transition-colors">
                                      <div className="flex flex-col">
                                        <span className="text-[10px] font-bold text-slate-300 italic">{newCampaign.type === 'email' ? c.email : c.phone}</span>
                                        <span className="text-[8px] font-black text-slate-500 uppercase">{c.name || 'Anonymous'}</span>
                                      </div>
                                      <Badge className="bg-emerald-500/10 text-emerald-500 border-none text-[7px] px-1.5 py-0">CRM RECORD</Badge>
                                    </div>
                                  ))
                                ) : (
                                  parsedContacts.slice(0, 15).map((c, i) => (
                                    <div key={i} className="flex items-center justify-between p-3 bg-slate-950/50 rounded-xl border border-white/5 group/item hover:border-indigo-500/30 transition-colors">
                                      <div className="flex flex-col">
                                        <span className="text-[10px] font-bold text-slate-300 italic">{c.identifier}</span>
                                        <span className="text-[8px] font-black text-slate-500 uppercase">{c.name || 'Anonymous'}</span>
                                      </div>
                                      <Badge className="bg-emerald-500/10 text-emerald-500 border-none text-[7px] px-1.5 py-0">READY</Badge>
                                    </div>
                                  ))
                                )}
                                {parsedContacts.length === 0 && (!preSelectedContacts || preSelectedContacts.length === 0) && (
                                  <div className="py-10 text-center">
                                    <p className="text-[10px] text-slate-500 italic">No contacts detected in dataset.</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="p-8 bg-slate-900/50 rounded-[2rem] border border-white/5 relative overflow-hidden group">
                          <div className="absolute top-0 right-0 p-6 opacity-[0.03]">
                            <MessageSquare className="h-24 w-24" />
                          </div>
                          <div>
                            <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest mb-4">Message Preview</p>
                            <div className="space-y-4">
                              {newCampaign.image_url && (
                                <div className="rounded-2xl overflow-hidden border border-white/10 max-w-xs mx-auto bg-slate-950/50 p-2">
                                  <img 
                                    src={newCampaign.image_url} 
                                    alt="WhatsApp Attachment Preview" 
                                    className="w-full h-auto rounded-xl object-cover max-h-[200px]"
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x200?text=Invalid+Image+URL';
                                    }}
                                  />
                                  <p className="text-[7px] text-slate-500 mt-2 text-center uppercase font-bold tracking-widest">Media Attachment Preview</p>
                                </div>
                              )}
                              <div className="p-8 bg-slate-950/50 rounded-3xl border border-white/5 relative z-10 max-h-[250px] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-800 scrollbar-track-transparent">
                                <p className="text-sm text-slate-300 italic leading-relaxed whitespace-pre-wrap">
                                  "{newCampaign.body || 'No message content defined for this broadcast...'}"
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            {/* Static Flex Footer (Guaranteed Visibility) */}
            <div className="flex-none p-8 px-12 bg-slate-900/95 backdrop-blur-3xl border-t border-white/5 flex items-center justify-between z-30 shadow-[0_-20px_50px_rgba(0,0,0,0.5)]">
              <Button
                variant="ghost"
                disabled={wizardStep === 1}
                onClick={() => setWizardStep(wizardStep - 1)}
                className="h-14 rounded-xl px-10 font-black text-slate-400 hover:text-white hover:bg-white/5 uppercase tracking-[0.2em] text-[9px] transition-all"
              >
                <ArrowLeft className="h-4 w-4 mr-3" /> Previous Phase
              </Button>

              <div className="flex items-center gap-5">
                <Button variant="ghost" onClick={() => { resetWizard(); onClose(); }} className="text-slate-500 hover:text-rose-500 font-bold uppercase tracking-widest text-[9px] transition-colors">Abort Mission</Button>

                {wizardStep === 2 && (
                  <Button
                    variant="ghost"
                    onClick={() => setWizardStep(wizardStep + 1)}
                    className="text-indigo-400 font-black uppercase tracking-widest text-[9px] hover:text-indigo-300 hover:bg-indigo-400/10 h-14 px-8 rounded-xl border border-indigo-500/20"
                  >
                    Skip Message Phase
                  </Button>
                )}

                {wizardStep < 4 ? (
                  <Button
                    onClick={() => setWizardStep(wizardStep + 1)}
                    disabled={!canProceed}
                    className={cn(
                      "h-14 rounded-xl px-12 font-black uppercase tracking-[0.2em] text-[9px] transition-all",
                      canProceed ? "bg-white text-slate-950 shadow-[0_0_30px_rgba(255,255,255,0.2)] hover:scale-[1.02]" : "bg-slate-800 text-slate-600 opacity-50"
                    )}
                  >
                    Next Phase <ArrowRight className="h-3 w-4 ml-3" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleCreateCampaign}
                    disabled={isCreating || !canProceed}
                    className={cn(
                      "h-14 rounded-xl px-14 font-black uppercase tracking-[0.3em] text-[10px] transition-all",
                      canProceed ? "bg-indigo-600 text-white shadow-[0_0_40px_rgba(79,70,229,0.4)] hover:scale-[1.02] hover:bg-indigo-700" : "bg-slate-800 text-slate-600 opacity-50"
                    )}
                  >
                    {isCreating ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save Campaign"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}